import { useEffect, useRef, useState } from "react";
import { loadAudio, pauseAudio, playAudio, seekAudio, setAudioSeekState, stopAudio } from "core/javascriptChannels";

export type MediaItem = {
  src: string;
  title: string;
};

export type UseNativeAudioPlayerResult = {
  isInitialised: boolean;
  playTime: number;
  duration: number;
  isPlaying: boolean;
  error: boolean;
  play: () => Promise<void>;
  pause: () => Promise<void>;
  stop: () => Promise<void>;
  onSeekMouseDown: () => void;
  onSeekMouseUp: () => void;
  onSeekChange: (position: number) => void;
};

const hasAudioFinished = (playTime: number, duration: number) => playTime >= duration && duration > 0;

export const useNativeAudioPlayer = (mediaItem: MediaItem, autoStart = true): UseNativeAudioPlayerResult => {
  const [isInitialised, setIsInitialised] = useState(false);
  const [playTime, setPlayTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(autoStart);
  const [isSeeking, setIsSeeking] = useState(false);
  const [error, setError] = useState(false);
  const latestActionIdRef = useRef(0);

  const updateProgressCallback = (currentProgress: number) => {
    setPlayTime(currentProgress);
    setIsInitialised(true);
    if (currentProgress >= duration) {
      setIsPlaying(false);
    }
  };

  if (window.updateAudioProgress === undefined && duration > 0) {
    window.updateAudioProgress = updateProgressCallback;
  }

  useEffect(() => {
    async function load() {
      const audioPlayerState = await loadAudio(mediaItem);

      if (audioPlayerState === undefined) {
        setError(true);
        return;
      }
      setDuration(audioPlayerState.durationInSeconds);
      setPlayTime(audioPlayerState.positionInSeconds);

      const finished = hasAudioFinished(audioPlayerState.positionInSeconds, audioPlayerState.durationInSeconds);

      if (autoStart && !finished) {
        const actionId = ++latestActionIdRef.current;
        await playAudio();
        if (actionId === latestActionIdRef.current) {
          setIsPlaying(true);
        }
      } else {
        setIsPlaying(audioPlayerState.isPlaying && !finished);
      }
      setIsInitialised(true);
    }

    load();

    return () => (window.updateAudioProgress = undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    function updateSeekAfterValueSet() {
      if (isSeeking === false && isInitialised) {
        seekAudio(playTime);
      }
    }

    updateSeekAfterValueSet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSeeking]);

  const play = async () => {
    latestActionIdRef.current += 1;
    setIsPlaying(true);

    if (hasAudioFinished(playTime, duration)) {
      setPlayTime(0);
      await seekAudio(0);
    }
    await playAudio();
  };

  const pause = async () => {
    latestActionIdRef.current += 1;
    setIsPlaying(false);
    await pauseAudio();
  };

  const stop = async () => {
    latestActionIdRef.current += 1;
    setIsPlaying(false);
    await stopAudio();
  };

  const onSeekMouseDown = () => {
    setIsSeeking(true);
    setAudioSeekState(true);
  };

  const onSeekMouseUp = () => {
    setIsSeeking(false);
    setAudioSeekState(false);
  };

  const onSeekChange = (position: number) => {
    const currentPlayTime = Math.ceil(duration * position);
    setPlayTime(Math.min(duration, currentPlayTime));
  };

  return { isInitialised, playTime, duration, isPlaying, error, play, pause, stop, onSeekMouseDown, onSeekMouseUp, onSeekChange };
};
